// Shared constants + helpers for the redesigned Publisher Buckets experience
// (/publisher-buckets-new). Isolated from the legacy BucketManager so the old
// screen is never touched.
//
// Behaviour follows "Alchemiq Bucket Management Corrections" (30 Aug 2026),
// which supersedes the original design mock-ups where the two disagree.

// Brand tokens (matches the app convention of inline hex — there is no theme file).
export const COLORS = {
  navy: '#0B1E3F',
  orange: '#FF6B35',
  blue: '#00AEEF',
  slate: '#3C4A5E',
  softWhite: '#F7F9FC',
  // In Production R10 — two operational shades. Deep Navy is the structural
  // colour and is too heavy for a control that repeats across a strip, so the
  // active chip and the Completed outline use `blueNavy`, and Sent (a terminal,
  // quiet state) uses `blueSlate`. Both are documented in CLAUDE.md.
  blueNavy: '#143E6A',
  blueSlate: '#315574',
  divider: 'rgba(60,74,94,0.12)',
};

// Canonical statuses in workflow order (spec 6.3). Rejected/Archived/Deleted
// are secondary outcomes and deliberately excluded from the cycle.
export const WORKFLOW_STATUSES = [
  'suggestion',
  'accepted',
  'in_work',
  'completed',
  'sent_to_publisher',
  'published',
];

// Spec 8.1 — In Production holds active production work only. Suggestion lives
// in Bucket Stories, Published in the separate Published view, and
// handled_by_publisher is excluded because Alchemiq has no work to do on it.
export const IN_PRODUCTION_STATUSES = ['accepted', 'in_work', 'completed', 'sent_to_publisher'];

// Spec 6.2 — the "Completed" primary filter spans these three.
export const COMPLETED_FILTER_STATUSES = ['completed', 'sent_to_publisher', 'handled_by_publisher'];

// Every status the All view may render, in workflow order, secondary outcomes last.
export const ALL_STATUS_ORDER = [...WORKFLOW_STATUSES, 'handled_by_publisher', 'rejected'];

export const STATUS_LABELS = {
  suggestion: 'Suggestion',
  accepted: 'Accepted',
  in_work: 'In work',
  completed: 'Completed',
  sent_to_publisher: 'Sent to publisher',
  published: 'Published',
  handled_by_publisher: 'Handled by publisher',
  rejected: 'Rejected',
  deleted: 'Deleted',
};

// In Production R1/R2/R8 — "Sent" is the SHORT DISPLAY LABEL for
// sent_to_publisher, used in the summary, the filter row and the row tag. It is
// not a new workflow state, and STATUS_LABELS above stays the canonical naming
// (the row menu's "Correct status to" list still says "Sent to publisher").
export const STATUS_SHORT_LABELS = {
  ...STATUS_LABELS,
  sent_to_publisher: 'Sent',
};

// In Production R10 — the palette the gap analysis specifies, by status. Kept
// separate from STATUS_TEXT_COLORS (Bucket Stories) and STATUS_BADGE_COLORS so
// restyling this one screen cannot shift the others.
export const STATUS_TAG_COLORS = {
  accepted:          { fg: COLORS.blue,      bg: 'rgba(0,174,239,0.08)',  border: 'rgba(0,174,239,0.45)' },
  in_work:           { fg: COLORS.orange,    bg: 'rgba(255,107,53,0.08)', border: 'rgba(255,107,53,0.45)' },
  completed:         { fg: COLORS.blueNavy,  bg: 'rgba(20,62,106,0.06)',  border: 'rgba(20,62,106,0.40)' },
  sent_to_publisher: { fg: COLORS.blueSlate, bg: 'rgba(49,85,116,0.06)',  border: 'rgba(49,85,116,0.40)' },
};

// Colour used for the inline status word on a story row.
export const STATUS_TEXT_COLORS = {
  suggestion: '#00AEEF',
  accepted: '#9C27B0',
  in_work: '#FF9800',
  completed: '#009688',
  sent_to_publisher: '#9E9E9E',
  published: '#4CAF50',
  handled_by_publisher: '#4CAF50',
  rejected: '#F44336',
  deleted: '#BDBDBD',
};

// Follow-up §1.6 — In Production must show status as a padded pill with a
// distinct background, not coloured text. Backgrounds are soft tints of the
// STATUS_TEXT_COLORS above so contrast on the text stays legible.
export const STATUS_BADGE_COLORS = {
  suggestion:         { bg: '#E1F5FE', fg: '#0277BD' },
  accepted:           { bg: '#F3E5F5', fg: '#6A1B9A' },
  in_work:            { bg: '#FFF3E0', fg: '#E65100' },
  completed:          { bg: '#E0F2F1', fg: '#00695C' },
  sent_to_publisher:  { bg: '#ECEFF1', fg: '#455A64' },
  published:          { bg: '#E8F5E9', fg: '#2E7D32' },
  handled_by_publisher:{ bg: '#E8F5E9', fg: '#2E7D32' },
  rejected:           { bg: '#FFEBEE', fg: '#C62828' },
  deleted:            { bg: '#F5F5F5', fg: '#616161' },
};

// Follow-up §1.7 — In Production status-group priority for default and
// sort-by-Status views. Active work (Accepted, In work) sits above Completed
// so operators see items still needing attention first. Anything not listed
// falls to the bottom in its natural order.
export const IN_PRODUCTION_STATUS_ORDER = [
  'accepted',
  'in_work',
  'completed',
  'sent_to_publisher',
  'published',
];

// Spec 6.4 — status cycle for an Alchemiq-written bucket. Publisher-written
// buckets resolve an accepted Suggestion to 'handled_by_publisher' instead and
// take no further production status.
export const STATUS_FORWARD_MAP = {
  suggestion: 'accepted',
  accepted: 'in_work',
  in_work: 'completed',
  completed: 'sent_to_publisher',
  sent_to_publisher: 'published',
  published: 'suggestion',
};

// Spec 2.3 — the ONLY count shown on Publisher Buckets. An active suggestion has
// status 'suggestion' and is not archived or deleted. Never call this "pending".
// The backend supplies activeSuggestionCount; this is the defensive fallback.
export function activeSuggestions(bucket) {
  if (typeof bucket?.activeSuggestionCount === 'number') return bucket.activeSuggestionCount;
  return bucket?.storyCounts?.suggestion || 0;
}

// Roll a client's buckets up into the accordion "publisher" row summary.
export function clientMetrics(buckets) {
  let active = 0;
  let lastActivityAt = null;
  for (const b of buckets) {
    active += activeSuggestions(b);
    const ts = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
    if (ts && (!lastActivityAt || ts > lastActivityAt)) lastActivityAt = ts;
  }
  const languages = [...new Set(buckets.map((b) => b.targetLanguage).filter(Boolean))];
  return {
    activeSuggestions: active,
    sections: buckets.length,
    language: languages[0] || 'English',
    languages,
    lastActivityAt,
  };
}

export const initials = (name = '') =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

// In Production R7 — the monogram in a Bucket cell.
//
// `initials` above takes one letter per word, which gives a single-word client
// ("At", "YNET") a one-character monogram. That is thin for something whose
// only job is telling clients apart at a glance, so a single word falls back to
// its first two letters. Deliberately a separate function: `initials` is shared
// with the Target buckets panel and the Move/Copy modal, and this rule is a
// display choice for one screen, not a change to how the app abbreviates names.
export const clientMonogram = (name = '') => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return initials(name);
};

// In Production R5 — a bucket chip reads "<client> <bucket>", and many bucket
// names already begin with their client's name: "Israel Hayom" + "Israel Hayom
// General" rendered as "Israel Hayom Israel Hayom General", which is how a
// strip of 29 chips became unreadable. Drop the redundant prefix; the client is
// already shown beside it.
//
// Only a WHOLE leading word-run is stripped, so "Mafra- Czech Republic" does
// not eat the "Mafra" in "Mafra Science", and a bucket named exactly after its
// client keeps its name rather than becoming blank.
export function shortBucketName(bucketName = '', clientName = '') {
  const bucket = bucketName.trim();
  const client = clientName.trim();
  if (!client || !bucket) return bucket;
  if (bucket.toLowerCase() === client.toLowerCase()) return bucket;
  const prefix = `${client.toLowerCase()} `;
  return bucket.toLowerCase().startsWith(prefix) ? bucket.slice(client.length + 1).trim() : bucket;
}

// Deterministic chip colour per client name (Target buckets panel).
const CHIP_COLORS = ['#FF6B35', '#00AEEF', '#9C27B0', '#009688', '#3C4A5E', '#FF9800', '#4CAF50', '#795548'];
export function chipColor(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return CHIP_COLORS[h % CHIP_COLORS.length];
}

export function formatShortDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Admin configuration deep link (spec 2.5 / 6.1 / 10.5) — stable bucket id, new tab.
export const bucketConfigUrl = (bucketId) => `/admin2/publisher-buckets/${bucketId}`;

// localStorage key (namespaced so the legacy screen's keys are untouched).
// Personal ordering ("My order") was removed by spec 2.2 — favourites remain.
export const LS_FAVOURITES = 'alchemiq_pbnew_favourites';
// §2.2 — bucket-level favourites are a SECOND per-admin list. Stored as bucket
// ids alongside the publisher favourites, deliberately in a distinct key so the
// two stay independent.
export const LS_FAVOURITE_BUCKETS = 'alchemiq_pbnew_favourite_buckets';

// react-dnd item type for dragging a story onto a target bucket.
export const PBNEW_STORY = 'PBNEW_STORY';

// Newsletter item ordering, dragged inside the Create-newsletter modal. A
// distinct type from PBNEW_STORY so a newsletter row can never be dropped onto
// a bucket column, or vice versa.
export const PBNEW_NEWSLETTER_ITEM = 'PBNEW_NEWSLETTER_ITEM';

// Newsletter story cap (answer A2). Mirrors the NEWSLETTER_MAX_STORIES backend
// configuration key so the UI can explain the limit before submitting; the
// backend re-checks its own configured value and is the authority.
export const NEWSLETTER_MAX_STORIES = 20;
